import { useCallback, useEffect, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { mergeRegister } from '@lexical/utils';

import { RichTextEditorToolbarPluginProps } from 'components/editor/RichTextEditor.types';
import Button from 'components/button/Button';
import { useTranslation } from 'react-i18next';

interface TextFormattingButtonsProps {
  disabled: boolean;
  toolbarSettings: RichTextEditorToolbarPluginProps['toolbarSettings'];
}

const TextFormattingButtons = ({
  disabled,
  toolbarSettings: { features },
}: TextFormattingButtonsProps) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, $updateToolbar]);

  return (
    <>
      {features?.bold && (
        <Button
          aria-label={t('form.editor.buttons.formatBold')}
          className={`toolbar-item ${isBold ? 'active' : ''}`}
          data-testid="toolbar-button-bold"
          disabled={disabled}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
          }}
        >
          <span className="bold">B</span>
        </Button>
      )}
      {features?.italic && (
        <Button
          aria-label={t('form.editor.buttons.formatItalics')}
          className={`toolbar-item ${isItalic ? 'active' : ''}`}
          data-testid="toolbar-button-italic"
          disabled={disabled}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
          }}
        >
          <span className="italic">I</span>
        </Button>
      )}
      {features?.underline && (
        <Button
          aria-label={t('form.editor.buttons.formatUnderline')}
          className={`toolbar-item ${isUnderline ? 'active' : ''}`}
          data-testid="toolbar-button-underline"
          disabled={disabled}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
          }}
        >
          <span className="underline">U</span>
        </Button>
      )}
      {features?.strikethrough && (
        <Button
          aria-label={t('form.editor.buttons.formatStrikethrough')}
          className={`toolbar-item ${isStrikethrough ? 'active' : ''}`}
          data-testid="toolbar-button-strikethrough"
          disabled={disabled}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
          }}
        >
          <span className="strikethrough">S</span>
        </Button>
      )}
    </>
  );
};

export default TextFormattingButtons;
